import React, { useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Button,
  DatePicker,
  Input,
  notification,
  Table,
  message,
  Flex,
  Card,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../redux/store";
import type { PrepaidData } from "../redux/slices/prepaidSlice";
import CustomTableLoader from "src/components/common/CustomTableLoader";
import { GetStatusReport } from "src/redux/actions/StatusReportAction";
import { CSVLink } from "react-csv";

const Status: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const dataSource = useSelector((state: any) => state.statusreport.data ?? []);
  const loading = useSelector((state: any) => state.statusreport.loading);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const { Title } = Typography;

  const columns: any = [
    {
      title: "ID",
      dataIndex: "Id",
      key: "Id",
      align: "center",
    },
    {
      title: "Cab ID",
      dataIndex: "cabId",
      key: "cabId",
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      align: "center",
      //   render: (text: any) => text.split(",")[0],
    },
    {
      title: "Time",
      dataIndex: "Time",
      key: "Time",
      align: "center",
      render: (text: string) => {
        if (!text) return "";

        const [time, ampm] = text.split(" ");
        if (!time || !ampm) return text;

        const [hours, minutes] = time.split(":");

        return `${hours}:${minutes} ${ampm.toUpperCase()}`;
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
    },
    {
      title: "Updated By",
      dataIndex: "doneBy",
      key: "doneBy",
      align: "center",
    },
  ];

  useEffect(() => {
    const from = dayjs();
    const to = dayjs();

    const toDatePlusOne = to.add(1, "day");
    setFromDate(from);
    setToDate(to);

    dispatch(
      GetStatusReport(
        from.format("YYYY-MM-DD"),
        toDatePlusOne.format("YYYY-MM-DD")
      )
    );
  }, []);

  const handleFetch = () => {
    if (fromDate && toDate) {
      const from = fromDate.format("YYYY-MM-DD");
      const toPlusOne = dayjs(toDate).add(1, "day").format("YYYY-MM-DD");
      dispatch(GetStatusReport(from, toPlusOne));
    } else {
      message.warning("Please select both From and To dates.");
    }
  };

  const handleSearch = (value: any) => {
    setSearchText(String(value || ""));
  };

  const filteredDataSource = dataSource.filter((item: PrepaidData) =>
    Object.values(item).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const exportToExcel = (data: PrepaidData[], filename: string) => {
    if (!data || data.length === 0) return;
    const exportDate = dayjs().format("YYYY-MM-DD");
    const title = "Corporate Trip";
    const titleRow = [[`${title} - Export Date: ${exportDate}`]];
    const emptyRow = [[""]];
    const headers = columns.map((col: any) => col.title);
    const rows = data.map((item) =>
      columns.map((col: any) => item[col.dataIndex as keyof PrepaidData])
    );
    const worksheetData = [...titleRow, ...emptyRow, headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CorporateTrip");
    XLSX.writeFile(workbook, `${filename}_${exportDate}.xlsx`);
    notification.success({
      message: "Export Successful",
      description: `Excel file for "${title}" has been downloaded on ${exportDate}.`,
    });
  };

  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf("day");
  };

  const disableToDate = (current: any) => {
    if (!fromDate) return disableFutureDates(current);
    return (
      (current && current < fromDate.startOf("day")) ||
      (current && current > dayjs().endOf("day"))
    );
  };

  return (
    <Layout
      style={{ minHeight: "100vh", overflow: "hidden", position: "relative" }}
    >
      <Flex
        style={{
          background: "whitesmoke",
          rowGap: "20px",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Flex style={{ width: "100%" }}>
          <Flex style={{ width: "50%", justifyContent: "start" }}>
            <Title
              level={3}
              style={{
                fontSize: "35px",
                fontWeight: "bolder",
                color: "black",
                fontFamily: "serif",
                textShadow: "2px 2px 5px rgba(0, 0, 0, 0.3)",
                margin: 0,
              }}
            >
              Active / Inactive
            </Title>
          </Flex>
        </Flex>
        <Flex style={{ width: "100%" }}>
          <Flex style={{ width: "70%", gap: "1%" }}>
            {/* <DatePicker
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(date) => setFromDate(date)}
              format="DD/MM/YYYY"
              placeholder="From Date"
            />
            <DatePicker
              value={toDate ? dayjs(toDate) : null}
              onChange={(date) => setToDate(date)}
              format="DD/MM/YYYY"
              placeholder="To Date"
            /> */}
            <DatePicker
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(date) => {
                setFromDate(date);
                if (toDate && date && toDate.isBefore(date, "day")) {
                  setToDate(null);
                }
              }}
              disabledDate={disableFutureDates}
              style={{ border: "1px solid grey", width: "20%" }}
              placeholder="From Date"
            />

            <DatePicker
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(date) => setToDate(date)}
              disabledDate={disableToDate}
              style={{ border: "1px solid grey", width: "20%" }}
              placeholder="To Date"
            />
            {/* <Input
              placeholder="Filter by Cab ID"
              value={cabIdFilter}
              onChange={(e) => setCabIdFilter(e.target.value)}
              style={{
                border: "1px solid grey",
                width: "30%",
              }}
            /> */}
            <Button
              style={{ color: "white", background: "#800080", fontWeight: 500 }}
              onClick={handleFetch}
            >
              Go
            </Button>
            {/* <Button
              style={{
                background:
                  "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
                marginLeft: "20px",
              }}
              onClick={handleToday}
            >
              Today
            </Button>
            <Button
              style={{
                background:
                  "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
              }}
              onClick={handlePrevious}
            >
              Previous
            </Button>
            <Button
              style={{
                background:
                  "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
              }}
              onClick={handleMonth}
            >
              Month
            </Button> */}
          </Flex>
          <Flex style={{ width: "35%", gap: "2%", justifyContent: "end" }}>
            {/* <Button
              icon={<DownloadOutlined />}
              onClick={() =>
                exportToExcel(filteredDataSource, "CorporateAccountData")
              }
              disabled={filteredDataSource.length === 0}
              style={{
                background: "linear-gradient(45deg, #159957, #155799)",
                color: "white",
              }}
            >
              Download
            </Button> */}
            <CSVLink data={filteredDataSource} filename="Active/Inactive Report.csv">
              <Button
                icon={<DownloadOutlined />}
                style={{
                  background: "linear-gradient(45deg, #159957, #155799)",
                  color: "white",
                }}
                disabled={filteredDataSource.length === 0}
              >
                Download
              </Button>
            </CSVLink>
            <Input.Search
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => handleSearch(value)}
              style={{ width: "55%" }}
            />
          </Flex>
        </Flex>
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          loading={{
            spinning: loading,
            indicator: <CustomTableLoader />,
          }}
          size="middle"
          pagination={{
            // pageSize: limit,
            // current: skip / limit + 1,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 100,
            // onChange: (page: number, pageSize: number) => {
            //   setLimit(pageSize);
            //   setSkip((page - 1) * pageSize);
            // },
            // onShowSizeChange: (current: number, size: number) => {
            //   setLimit(size);
            //   setSkip(0);
            // },
          }}
          dataSource={filteredDataSource}
          columns={columns}
          scroll={{ x: "calc(500px + 50%)", y: 50 * 15 }}
          bordered
          rowKey="bookingId"
        />
      </Flex>
    </Layout>
  );
};

export default Status;
